import apiClient from './apiClient';

// Flag to use mock data for development
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

// Mock data for admin reports
const mockTimeReportSummary = {
  totalHours: 1285,
  totalEntries: 156,
  approvedHours: 1024,
  pendingHours: 189,
  rejectedHours: 72,
  averageHoursPerEmployee: 27.3,
  averageHoursPerCustomer: 257,
  currentMonthHours: 321,
  previousMonthHours: 298,
  changePercentage: 7.7
};

const mockTimeDistribution = {
  byCustomer: [
    { name: 'Acme Corporation', hours: 384, percentage: 29.9 },
    { name: 'Globex Corporation', hours: 312, percentage: 24.3 },
    { name: 'Initech', hours: 257, percentage: 20.0 },
    { name: 'Cyberdyne Systems', hours: 204, percentage: 15.9 },
    { name: 'Stark Industries', hours: 128, percentage: 9.9 }
  ],
  byEmployee: [
    { name: 'John Smith', hours: 172, percentage: 13.4 },
    { name: 'Jane Doe', hours: 168, percentage: 13.1 },
    { name: 'Alex Johnson', hours: 152, percentage: 11.8 },
    { name: 'Emily Davis', hours: 145, percentage: 11.3 },
    { name: 'Michael Brown', hours: 132, percentage: 10.3 },
    { name: 'Others', hours: 516, percentage: 40.1 }
  ],
  byMonth: [
    { month: 'Jan', hours: 110 },
    { month: 'Feb', hours: 118 },
    { month: 'Mar', hours: 125 },
    { month: 'Apr', hours: 132 },
    { month: 'May', hours: 141 },
    { month: 'Jun', hours: 148 },
    { month: 'Jul', hours: 156 },
    { month: 'Aug', hours: 135 },
    { month: 'Sep', hours: 120 },
    { month: 'Oct', hours: 0 },
    { month: 'Nov', hours: 0 },
    { month: 'Dec', hours: 0 }
  ],
  byStatus: [
    { status: 'Approved', hours: 1024, percentage: 79.7 },
    { status: 'Pending', hours: 189, percentage: 14.7 },
    { status: 'Rejected', hours: 72, percentage: 5.6 }
  ]
};

const mockDetailedEntries = [
  {
    id: 1,
    employeeName: 'John Smith',
    customerName: 'Acme Corporation',
    project: 'Website Redesign',
    date: '2023-09-15T00:00:00.000Z',
    hours: 8,
    status: 'approved',
    description: 'Front-end development for homepage'
  },
  {
    id: 2,
    employeeName: 'Jane Doe',
    customerName: 'Globex Corporation',
    project: 'ERP Implementation',
    date: '2023-09-14T00:00:00.000Z',
    hours: 6,
    status: 'approved',
    description: 'Database configuration and testing'
  },
  {
    id: 3,
    employeeName: 'Alex Johnson',
    customerName: 'Initech',
    project: 'Mobile Banking App',
    date: '2023-09-15T00:00:00.000Z',
    hours: 7.5,
    status: 'pending',
    description: 'API integration for account services'
  },
  {
    id: 4,
    employeeName: 'Emily Davis',
    customerName: 'Cyberdyne Systems',
    project: 'AI Research Platform',
    date: '2023-09-13T00:00:00.000Z',
    hours: 8,
    status: 'approved',
    description: 'Machine learning algorithm optimization'
  },
  {
    id: 5,
    employeeName: 'Michael Brown',
    customerName: 'Acme Corporation',
    project: 'Website Redesign',
    date: '2023-09-14T00:00:00.000Z',
    hours: 4,
    status: 'rejected',
    description: 'UX testing and bug fixes'
  },
  {
    id: 6,
    employeeName: 'John Smith',
    customerName: 'Initech',
    project: 'Mobile Banking App',
    date: '2023-09-12T00:00:00.000Z',
    hours: 6,
    status: 'approved',
    description: 'Security module implementation'
  },
  {
    id: 7,
    employeeName: 'Jane Doe',
    customerName: 'Cyberdyne Systems',
    project: 'AI Research Platform',
    date: '2023-09-11T00:00:00.000Z',
    hours: 8,
    status: 'approved',
    description: 'Data processing pipeline development'
  },
  {
    id: 8,
    employeeName: 'Alex Johnson',
    customerName: 'Globex Corporation',
    project: 'ERP Implementation',
    date: '2023-09-13T00:00:00.000Z',
    hours: 7,
    status: 'pending',
    description: 'User acceptance testing support'
  },
  {
    id: 9,
    employeeName: 'Emily Davis',
    customerName: 'Acme Corporation',
    project: 'Website Redesign',
    date: '2023-09-12T00:00:00.000Z',
    hours: 8,
    status: 'approved',
    description: 'Content management system integration'
  },
  {
    id: 10,
    employeeName: 'Michael Brown',
    customerName: 'Initech',
    project: 'Mobile Banking App',
    date: '2023-09-11T00:00:00.000Z',
    hours: 4.5,
    status: 'approved',
    description: 'Performance optimization'
  }
];

/**
 * Get time reports (admin)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing time reports
 */
export const getAdminReports = async (params = {}) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => resolve({
        summary: mockTimeReportSummary,
        distribution: mockTimeDistribution
      }), 800);
    });
  }
  
  try {
    const response = await apiClient.get('/admin/reports', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Generate admin report (admin)
 * @param {object} reportParams - Report parameters
 * @returns {Promise} - Promise containing report data
 */
export const generateAdminReport = async (reportParams) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Filter entries based on parameters
      let filteredEntries = [...mockDetailedEntries];
      
      if (reportParams.startDate) {
        const startDate = new Date(reportParams.startDate);
        filteredEntries = filteredEntries.filter(entry => 
          new Date(entry.date) >= startDate
        );
      }
      
      if (reportParams.endDate) {
        const endDate = new Date(reportParams.endDate);
        filteredEntries = filteredEntries.filter(entry => 
          new Date(entry.date) <= endDate
        );
      }
      
      if (reportParams.employeeId) {
        // For mock data, we'll just filter by name since we don't have IDs
        const employeeName = reportParams.employeeId === 2 ? 'John Smith' : 
                            reportParams.employeeId === 3 ? 'Jane Doe' : 
                            reportParams.employeeId === 4 ? 'Alex Johnson' : '';
        
        if (employeeName) {
          filteredEntries = filteredEntries.filter(entry => 
            entry.employeeName === employeeName
          );
        }
      }
      
      if (reportParams.customerId) {
        // For mock data, we'll filter by name
        const customerName = reportParams.customerId === 1 ? 'Acme Corporation' : 
                            reportParams.customerId === 2 ? 'Globex Corporation' : 
                            reportParams.customerId === 3 ? 'Initech' : '';
        
        if (customerName) {
          filteredEntries = filteredEntries.filter(entry => 
            entry.customerName === customerName
          );
        }
      }
      
      if (reportParams.status) {
        filteredEntries = filteredEntries.filter(entry => 
          entry.status === reportParams.status
        );
      }
      
      // Calculate totals
      const totalHours = filteredEntries.reduce((sum, entry) => sum + entry.hours, 0);
      const totalEntries = filteredEntries.length;
      
      setTimeout(() => resolve({
        entries: filteredEntries,
        total: {
          hours: totalHours,
          entries: totalEntries
        }
      }), 1000);
    });
  }
  
  try {
    const response = await apiClient.post('/admin/reports/generate', reportParams);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Export admin report to CSV (admin)
 * @param {object} reportParams - Report parameters
 * @returns {Promise} - Promise containing CSV data
 */
export const exportAdminReportToCsv = async (reportParams) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Generate a report first to get filtered entries
      generateAdminReport(reportParams).then(report => {
        const { entries } = report;
        
        // Convert entries to CSV format
        const header = 'ID,Employee,Customer,Project,Date,Hours,Status,Description\n';
        const csvRows = entries.map(entry => {
          const date = new Date(entry.date).toLocaleDateString();
          return `${entry.id},"${entry.employeeName}","${entry.customerName}","${entry.project}","${date}",${entry.hours},"${entry.status}","${entry.description}"`;
        });
        
        const csvContent = header + csvRows.join('\n');
        
        // Convert to blob
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        
        setTimeout(() => resolve(blob), 800);
      });
    });
  }
  
  try {
    const response = await apiClient.post('/admin/reports/export', reportParams, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    throw await handleExportError(error);
  }
};

/**
 * Get customer reports (customer)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing customer reports
 */
export const getCustomerReports = async (params = {}) => {
  try {
    const response = await apiClient.get('/customer/reports', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Generate customer report (customer)
 * @param {object} reportParams - Report parameters
 * @returns {Promise} - Promise containing report data
 */
export const generateCustomerReport = async (reportParams) => {
  try {
    const response = await apiClient.post('/customer/reports/generate', reportParams);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Export customer report to CSV (customer)
 * @param {object} reportParams - Report parameters
 * @returns {Promise} - Promise containing CSV data
 */
export const exportCustomerReportToCsv = async (reportParams) => {
  try {
    const response = await apiClient.post('/customer/reports/export', reportParams, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    throw await handleExportError(error);
  }
};

/**
 * Get employee reports (employee)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing employee reports
 */
export const getEmployeeReports = async (params = {}) => {
  try {
    const response = await apiClient.get('/employee/reports', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Generate employee report (employee)
 * @param {object} reportParams - Report parameters
 * @returns {Promise} - Promise containing report data
 */
export const generateEmployeeReport = async (reportParams) => {
  try {
    const response = await apiClient.post('/employee/reports/generate', reportParams);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Export employee report to CSV (employee)
 * @param {object} reportParams - Report parameters
 * @returns {Promise} - Promise containing CSV data
 */
export const exportEmployeeReportToCsv = async (reportParams) => {
  try {
    const response = await apiClient.post('/employee/reports/export', reportParams, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    throw await handleExportError(error);
  }
};

/**
 * Helper function to download a file
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Weekly customer→employee hour matrix for the selected period (feedback #19).
 * @param {object} params - { startDate, endDate, customerId?, employeeIds?, active? }
 *   employeeIds: comma-separated users.id list; active: 'active'|'inactive'|'all'.
 * @returns {Promise<{weeks:Array, customers:Array}>}
 */
export const getWeeklyHoursReport = async (params) => {
  try {
    const response = await apiClient.get('/admin/reports/weekly-hours', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    return new Error(message);
  }
  return error;
};

/**
 * Helper function to handle errors from blob-response (export) endpoints.
 * When the backend returns a JSON error envelope, axios delivers it as a Blob
 * because responseType is 'blob'. Read and parse it so the real message surfaces.
 * @param {object} error - Error object
 * @returns {Promise<Error>} - Formatted error
 */
const handleExportError = async (error) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      const message = parsed?.error?.message || parsed?.message;
      if (message) {
        return new Error(message);
      }
    } catch (parseError) {
      // Could not read/parse the blob; fall through to a sensible default
    }
    return new Error('Failed to export report');
  }
  return handleError(error);
};